import axios from "axios";
import {
  getToken,
  getExpiresAt,
  updateSession,
  clearSession,
} from "../../context/sessionStore";
import parseExpiresAt from "../../utils/sessionTime";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;                                                

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  paramsSerializer: { indexes: null },
});

const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  paramsSerializer: { indexes: null },
});

const PROACTIVE_REFRESH_THRESHOLD = 60 * 1000;
const SKEW_MS = 10 * 1000;

function persistRefresh({ token, expiresAt, user, activeBusinessId, memberships }) {
  updateSession((prev) => ({
    ...(prev || {}),
    token,
    expiresAt: parseExpiresAt(expiresAt),
    user: user ?? prev?.user ?? null,
    activeBusinessId: activeBusinessId ?? prev?.activeBusinessId ?? null,
    memberships: memberships ?? prev?.memberships ?? [],
  }));
  window.dispatchEvent(new Event("auth:session-refreshed"));
}

let isRefreshing = false;
let waiters = [];
const queueWait = () => new Promise((res) => waiters.push(res));
const flushWaiters = () => { waiters.forEach((r) => r()); waiters = []; };

async function callRefresh() {
  const { data } = await refreshClient.post("/auth/refresh", {});
  persistRefresh({
    token: data.token,
    expiresAt: data.expiresAt,
    user: data.user,
    activeBusinessId: data.activeBusinessId,
    memberships: data.memberships,
  });
  return data;
}

const PUBLIC_AUTH = new Set([
  "POST /auth/login",
  "POST /auth/refresh",
  "POST /auth/logout",
  "POST /auth/mfa/totp/verify",
]);

async function ensureFreshToken() {
  const token = getToken();
  if (!token) return;
  const remaining = getExpiresAt() - Date.now() - SKEW_MS;
  if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) {
    if (isRefreshing) {
      await queueWait();
      return;
    }
    isRefreshing = true;
    try {
      await callRefresh();
      flushWaiters();
    } finally {
      isRefreshing = false;
    }
  }
}

api.interceptors.request.use(async (config) => {
  if (config.skipAuth) return config;

  const method = String(config.method || "GET").toUpperCase();
  const url = String(config.url || "");
  const key = `${method} ${url}`;
  const token = getToken();

  const isPublicAuth = PUBLIC_AUTH.has(key);

  if (token && !isPublicAuth) {
    if (key !== "POST /auth/refresh") {
      await ensureFreshToken();
    }
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${getToken()}`;
  }

  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};
    if (!original) throw error;

    const method = String(original.method || "GET").toUpperCase();
    const url = String(original.url || "");
    const key = `${method} ${url}`;
    const isPublicAuth = PUBLIC_AUTH.has(key);
    const hadAuthHeader = !!(original.headers && original.headers.Authorization);

    if (
      status === 401 &&
      !original._retry &&
      !original.skipAuth &&
      !isPublicAuth &&
      hadAuthHeader
    ) {
      original._retry = true;
      try {
        if (!isRefreshing) {
          isRefreshing = true;
          await callRefresh();
          flushWaiters();
        } else {
          await queueWait();
        }
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${getToken()}`;
        return api(original);
      } catch (e) {
        clearSession();
        window.dispatchEvent(new Event("auth:force-logout"));
        throw e;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  }
);

export { callRefresh };
export default api;
